import { z } from 'zod'
import { NON_EU_COUNTRIES_WITHOUT_POSTCODES } from '@/shared/config/countries'
import {
  createOptionalLatinCharacterSchema,
  createRequiredLatinCharacterSchema,
} from '@/shared/lib/latinCharacterValidation'

const streetLatinMsg = 'Street address must use Latin characters'
const streetNumberLatinMsg = 'Street number must use Latin characters'
const subDivisionLatinMsg = 'Subdivision must use Latin characters'
const postCodeLatinMsg = 'Post code must use Latin characters'
const poBoxLatinMsg = 'P.O. Box must use Latin characters'
const cityLatinMsg = 'City must use Latin characters'

const baseLatinCharacterAddressSchema = z.object({
  country: z.string().length(2).toLowerCase(),
  street: createOptionalLatinCharacterSchema(streetLatinMsg, { max: 70 }),
  street2: createOptionalLatinCharacterSchema(streetLatinMsg, { max: 70 }),
  streetNumber: createOptionalLatinCharacterSchema(streetNumberLatinMsg, {
    max: 35,
  }),
  subDivision: createOptionalLatinCharacterSchema(subDivisionLatinMsg),
  postCode: createOptionalLatinCharacterSchema(postCodeLatinMsg, { max: 17 }),
  poBox: createOptionalLatinCharacterSchema(poBoxLatinMsg, { max: 70 }),
  city: createRequiredLatinCharacterSchema(cityLatinMsg, { min: 2, max: 35 }),
})

export const organizationAddressSchema = baseLatinCharacterAddressSchema.refine(
  (data) => {
    const doesCountryHavePostCode = !NON_EU_COUNTRIES_WITHOUT_POSTCODES.includes(
      data.country
    )
    if (doesCountryHavePostCode && !data.postCode) {
      return false
    }
    return true
  },
  {
    message: 'Post code is required for countries with post codes',
    path: ['postCode'],
  }
)

export const organizationLocalAddressSchema = z
  .object({
    localStreet: z.string().max(70).optional(),
    localStreet2: z.string().max(70).optional(),
    localStreetNumber: z.string().max(35).optional(),
    localSubDivision: z.string().optional(),
    country: z.string().length(2).toLowerCase(),
    localPostCode: z.string().max(17).optional(),
    localPoBox: z.string().max(70).optional(),
    localCity: z.string().min(2).max(35),
  })
  .refine(
    (data) => {
      const doesCountryHavePostCode = !NON_EU_COUNTRIES_WITHOUT_POSTCODES.includes(
        data.country
      )
      if (doesCountryHavePostCode && !data.localPostCode) {
        return false
      }
      return true
    },
    {
      message: 'Post code is required for countries with post codes',
      path: ['localPostCode'],
    }
  )

export const organizationContactSchema = z.object({
  contactName: z.string().min(1),
  contactPhoneNumber: z.string().min(1),
  contactEmail: z.string().email(),
})

export type OrganizationAddressForm = z.infer<typeof organizationAddressSchema>
export type OrganizationLocalAddressForm = z.infer<
  typeof organizationLocalAddressSchema
>
export type OrganizationContactForm = z.infer<typeof organizationContactSchema>
